import React, { useState, useMemo } from 'react';
import { 
  Globe, Clock, MousePointer2, MapPin, ChevronLeft, ChevronRight, 
  Search, MoreHorizontal, ArrowRight, Layout, X, Activity, Calendar
} from 'lucide-react';
import { ClickEvent } from '../types';
import { GuideTooltip } from './GuideTooltip';
import { CustomDateSelector, CustomDateRange } from './CustomDateSelector';

type TimeRange = 'today' | 'week' | 'month' | 'custom';

interface IpTrackingCardProps {
  events: ClickEvent[];
  className?: string;
  id?: string;
}

interface IpSession {
  ip: string;
  location: string;
  events: ClickEvent[];
  lastSeen: number;
  actionCount: number;
  device: string;
  isBounce: boolean;
  entryPath: string;
}

export const IpTrackingCard: React.FC<IpTrackingCardProps> = ({ events, className, id = 'ip-tracker' }) => {
  const [filter, setFilter] = useState<TimeRange>('month');
  const [customDates, setCustomDates] = useState<Record<string, CustomDateRange>>({});
  const [page, setPage] = useState(1);
  const [selectedIp, setSelectedIp] = useState<IpSession | null>(null);
  const ITEMS_PER_PAGE = 8; // Reduced for better mobile height

  // --- Data Processing ---
  const filteredEvents = useMemo(() => {
    const now = Date.now();
    let startTime = 0;
    let endTime = Infinity;
    
    if (filter === 'today') startTime = new Date().setHours(0,0,0,0);
    else if (filter === 'week') startTime = now - 7 * 24 * 60 * 60 * 1000;
    else if (filter === 'month') startTime = now - 30 * 24 * 60 * 60 * 1000;
    else if (filter === 'custom') {
      const defaultRange = { 
        start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], 
        end: new Date().toISOString().split('T')[0] 
      };
      const dates = {
        start: customDates[id]?.start || defaultRange.start,
        end: customDates[id]?.end || defaultRange.end
      };
      startTime = new Date(dates.start).getTime();
      endTime = new Date(dates.end).setHours(23, 59, 59, 999);
    }

    return events.filter(e => e.timestamp >= startTime && e.timestamp <= endTime && e.country === 'US');
  }, [events, filter, customDates, id]);

  const ipSessions = useMemo(() => {
    const groups: Record<string, ClickEvent[]> = {};
    filteredEvents.forEach(e => {
      const ip = e.ip || 'Unknown';
      if (!groups[ip]) groups[ip] = [];
      groups[ip].push(e);
    });

    const sessions: IpSession[] = Object.entries(groups).map(([ip, userEvents]) => {
      const sorted = userEvents.sort((a, b) => a.timestamp - b.timestamp);
      const uniquePaths = new Set(sorted.map(e => e.path));
      const locationEvent = sorted.find(e => e.city || e.country) || sorted[0];
      const city = locationEvent.city ? decodeURIComponent(locationEvent.city) : '';
      const locStr = city ? `${city}, ${locationEvent.country || ''}` : (locationEvent.country || 'Unknown Location');

      return {
        ip,
        location: locStr,
        events: sorted,
        lastSeen: sorted[sorted.length - 1].timestamp,
        actionCount: sorted.length,
        device: sorted[0].deviceType || 'desktop',
        isBounce: uniquePaths.size === 1,
        entryPath: sorted[0].path
      };
    });

    return sessions.sort((a, b) => b.lastSeen - a.lastSeen);
  }, [filteredEvents]);

  const totalPages = Math.ceil(ipSessions.length / ITEMS_PER_PAGE);
  const currentData = ipSessions.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      setSelectedIp(null);
    }
  };

  const formatDate = (ts: number) => {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(ts));
  };

  return (
    <div className={`bg-white dark:bg-slate-900 p-5 md:p-8 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-[0_0_20px_-5px_rgba(0,0,0,0.05)] dark:shadow-[0_0_20px_-5px_rgba(255,255,255,0.03)] flex flex-col min-h-[500px] lg:h-[600px] ${className}`}>
      
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6 shrink-0">
        <div>
          <GuideTooltip term="Users">
             <h3 className="text-lg md:text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Globe className="text-indigo-500 w-5 h-5 md:w-6 md:h-6" /> IP Tracker (US Only)
             </h3>
          </GuideTooltip>
          <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
             Track individual user journeys
          </p>
        </div>

        <div className="flex flex-col items-end gap-2">
          <div className="flex bg-slate-100 dark:bg-slate-800 rounded-xl p-1 w-full sm:w-auto overflow-x-auto">
            {(['today', 'week', 'month', 'custom'] as TimeRange[]).map((t) => (
              <button
                key={t}
                onClick={() => { setFilter(t); setPage(1); setSelectedIp(null); }}
                className={`flex-1 sm:flex-none px-4 py-1.5 text-[10px] md:text-xs font-bold rounded-lg capitalize transition-all ${
                  filter === t 
                    ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm' 
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          {filter === 'custom' && (
            <CustomDateSelector 
              filterId={id} 
              customDates={customDates} 
              updateCustomDate={(fid, field, val) => setCustomDates(prev => ({
                ...prev,
                [fid]: { ...prev[fid], [field]: val }
              }))} 
            />
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col md:flex-row gap-6 overflow-hidden">
        <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${selectedIp ? 'hidden md:flex md:w-1/2 lg:w-5/12' : 'w-full'}`}>
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-indigo-50/50 dark:bg-slate-800/50 rounded-xl mb-2 text-[9px] md:text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
            <div className="col-span-5 md:col-span-6">IP / Location</div>
            <div className="col-span-3 text-center">Hits</div>
            <div className="col-span-4 md:col-span-3 text-right">Time</div>
          </div>

          <div className="flex-1 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
            {currentData.length > 0 ? currentData.map((session) => (
              <div 
                key={session.ip}
                onClick={() => setSelectedIp(session)}
                className={`grid grid-cols-12 gap-2 items-center px-3 py-3 rounded-xl cursor-pointer transition-all border ${
                  selectedIp?.ip === session.ip
                    ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800 shadow-sm'
                    : 'bg-white dark:bg-slate-900 border-transparent hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
              >
                <div className="col-span-5 md:col-span-6 min-w-0">
                  <div className="font-bold text-slate-700 dark:text-slate-200 truncate flex items-center gap-1.5 text-xs md:text-sm">
                    {session.ip}
                    {session.isBounce && <span className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-amber-400" title="Bounced"></span>}
                  </div>
                  <div className="text-[9px] md:text-xs text-slate-400 flex items-center gap-1 truncate">
                    <MapPin size={10} /> {session.location}
                  </div>
                </div>
                <div className="col-span-3 text-center">
                   <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-[10px] md:text-xs font-bold text-slate-600 dark:text-slate-300">
                     {session.actionCount}
                   </span>
                </div>
                <div className="col-span-4 md:col-span-3 text-right text-[9px] md:text-xs font-medium text-slate-500 dark:text-slate-400">
                  {formatDate(session.lastSeen).split(',')[0]}
                </div>
              </div>
            )) : (
              <div className="flex flex-col items-center justify-center h-full text-slate-400">
                <Search size={32} className="mb-2 opacity-50" />
                <p className="text-sm">No US traffic</p>
              </div>
            )}
          </div>

          <div className="mt-4 flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-800 shrink-0">
             <span className="text-[10px] md:text-xs text-slate-400">Page {page}/{totalPages || 1}</span>
             <div className="flex gap-2">
                <button onClick={() => handlePageChange(page - 1)} disabled={page === 1} className="p-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-600 disabled:opacity-50 transition-colors">
                  <ChevronLeft className="w-3.5 h-3.5 md:w-4 md:h-4" />
                </button>
                <button onClick={() => handlePageChange(page + 1)} disabled={page === totalPages || totalPages === 0} className="p-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-600 disabled:opacity-50 transition-colors">
                  <ChevronRight className="w-3.5 h-3.5 md:w-4 md:h-4" />
                </button>
             </div>
          </div>
        </div>

        {selectedIp && (
          <div className="flex-1 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-100 dark:border-slate-800 flex flex-col overflow-hidden animate-in slide-in-from-right-4 duration-300">
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex justify-between items-start shrink-0">
              <div>
                 <div className="flex items-center gap-2">
                   <h4 className="font-bold text-slate-900 dark:text-white text-base md:text-lg">{selectedIp.ip}</h4>
                   <span className={`px-2 py-0.5 text-[8px] md:text-[10px] font-bold uppercase rounded-full ${selectedIp.isBounce ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                     {selectedIp.isBounce ? 'Bounced' : 'Engaged'}
                   </span>
                 </div>
                 <div className="flex items-center gap-3 mt-1 text-[10px] md:text-xs text-slate-500">
                    <span className="flex items-center gap-1"><MapPin className="w-2.5 h-2.5 md:w-3 md:h-3" /> {selectedIp.location}</span>
                    <span className="flex items-center gap-1 capitalize"><Layout className="w-2.5 h-2.5 md:w-3 md:h-3" /> {selectedIp.device}</span>
                 </div>
              </div>
              <button onClick={() => setSelectedIp(null)} className="text-slate-400 hover:text-slate-600 transition-colors">
                <X className="w-4.5 h-4.5 md:w-5 md:h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 md:p-6 custom-scrollbar">
               <div className="relative pl-3 md:pl-4 space-y-6 before:absolute before:left-[4px] md:before:left-[5px] before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200 dark:before:bg-slate-800">
                  <div className="relative pl-5 md:pl-6">
                     <div className="absolute left-[-5px] top-1 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-4 ring-white dark:ring-slate-950"></div>
                     <div className="text-[10px] md:text-xs font-bold text-emerald-600 dark:text-emerald-400 mb-0.5">Session Started</div>
                     <div className="text-[9px] md:text-[10px] text-slate-400 mb-2">{formatDate(selectedIp.events[0].timestamp)}</div>
                     <div className="bg-white dark:bg-slate-900 p-2.5 md:p-3 rounded-xl border border-emerald-100 dark:border-emerald-900/30 shadow-sm">
                        <p className="text-xs md:text-sm font-medium text-slate-700 dark:text-slate-300">Arrived on <span className="text-indigo-600 font-mono bg-indigo-50 px-1 rounded">{selectedIp.entryPath}</span></p>
                        <p className="text-[10px] md:text-xs text-slate-500 mt-1">Source: <span className="font-bold">{selectedIp.events[0].source}</span></p>
                     </div>
                  </div>
                  {selectedIp.events.slice(1).map((event, idx) => {
                    const prevEvent = selectedIp.events[idx];
                    const isNavigation = prevEvent && prevEvent.path !== event.path;
                    return (
                      <div key={event.id} className="relative pl-5 md:pl-6">
                         <div className={`absolute left-[-5px] top-1.5 w-2.5 h-2.5 rounded-full ring-4 ring-white dark:ring-slate-950 ${isNavigation ? 'bg-indigo-500' : 'bg-slate-300 dark:bg-slate-600'}`}></div>
                         <div className="flex items-center gap-2 mb-1"><span className="text-[9px] md:text-[10px] font-mono text-slate-400">{new Date(event.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span></div>
                         <div className="bg-white dark:bg-slate-900 p-2.5 md:p-3 rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm">
                            {isNavigation ? (
                              <div className="flex items-center gap-2 text-xs md:text-sm text-slate-700">
                                 <ArrowRight size={12} className="text-indigo-500" />
                                 <span>Navigated to <span className="font-bold font-mono text-[10px] md:text-xs">{event.path}</span></span>
                              </div>
                            ) : (
                              <div className="flex items-start gap-2">
                                 <MousePointer2 size={12} className="mt-1 text-slate-400" />
                                 <div>
                                    <p className="text-xs md:text-sm text-slate-700">Interaction on <span className="font-mono text-[10px] opacity-75">{event.path}</span></p>
                                    <p className="text-[9px] md:text-xs text-slate-400 mt-0.5">X:{Math.round(event.x)}% Y:{Math.round(event.y)}%</p>
                                 </div>
                              </div>
                            )}
                         </div>
                      </div>
                    );
                  })}
                  <div className="relative pl-5 md:pl-6">
                     <div className="absolute left-[-5px] top-1 w-2.5 h-2.5 rounded-full bg-slate-400 dark:bg-slate-600 ring-4 ring-white dark:ring-slate-950"></div>
                     <div className="text-[10px] md:text-xs font-bold text-slate-500 dark:text-slate-400">Session Ended</div>
                     <div className="text-[9px] md:text-[10px] text-slate-400">{formatDate(selectedIp.lastSeen)}</div>
                  </div>
               </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};